import { Dispatch, SetStateAction } from 'react';
import { Control, UseFormGetValues, UseFormHandleSubmit } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Field, Notification, Visible } from 'coreOld/components';
import withQueryState from 'coreOld/components/withQueryState';
import { Grid, Icon } from 'coreOld/elements';
import { NotificationPosition } from 'coreOld/enums';
import { ButtonType, InputType, Position, Size } from 'coreOld/enums';
import { packageTypesOptions, partStatus, taaStatus } from 'partsOld/constants';
import { uomsService } from 'partsOld/services';
import { ICompanyPart, ICompanyPartForm } from 'partsOld/types';

import {
  ButtonWrapper,
  GridContainer,
  SectionTitle,
  WrapperNotification,
} from './companyPartDetailsFormStyle';

interface IProps {
  onSubmit: (data: ICompanyPartForm) => void;
  control: Control<ICompanyPartForm, object>;
  handleSubmit: UseFormHandleSubmit<ICompanyPartForm>;
  getValues?: UseFormGetValues<ICompanyPartForm>;
  isEditable?: boolean;
  setIsEditable?: Dispatch<SetStateAction<boolean>>;
  part?: Partial<ICompanyPart>;
  isSubmitting?: boolean;
  isAddMode?: boolean;
  isSaveEnabled?: boolean;
}

const CompanyPartDetailsFormView = ({
  onSubmit,
  control,
  handleSubmit,
  isEditable,
  setIsEditable,
  part,
  getValues,
  isSubmitting,
  isAddMode,
  isSaveEnabled,
}: IProps) => {
  const { t } = useTranslation();

  const isDisabled = !isEditable && Boolean(part) && !isAddMode;
  const labelPosition = Position.LEFT;

  const disabledState = {
    disabled: isDisabled,
    editable: false,
    fontInherit: true,
  };

  return (
    <>
      <WrapperNotification>
        <Notification position={NotificationPosition.TAB as any} />
      </WrapperNotification>

      <SectionTitle>
        {t('labelSetUpPart')}
        <Visible when={!isDisabled && !isSubmitting}>
          <ButtonWrapper>
            <Button
              btnType={ButtonType.ACTION_INVERTED}
              onClick={() => setIsEditable(false)}
              disabled={!isSaveEnabled}
              hasMarginRight
            >
              {t('buttonLabelCancel')}
            </Button>
            <Button onClick={handleSubmit(onSubmit)} disabled={!isSaveEnabled}>
              {t('buttonLabelSave')}
            </Button>
          </ButtonWrapper>
        </Visible>
        <Visible when={isDisabled}>
          <ButtonWrapper onClick={() => setIsEditable(true)}>
            <Icon name={Icon.PENCIL} hover />
          </ButtonWrapper>
        </Visible>
      </SectionTitle>

      <GridContainer container isDisabled={isDisabled} spacing={2}>
        <Grid item md={6} xs={12}>
          <Field
            name="packageType"
            type={InputType.SELECT}
            label={t('labelPackageType')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              placeholder: '',
              options: packageTypesOptions,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="partBaseCode"
            label={t('labelBaseCode')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="className"
            label={t('labelClass')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            name="uomId"
            type={InputType.ASYNC_SELECT}
            label={t('labelBaseUom')}
            labelPosition={labelPosition}
            inputProps={{
              getOptions: uomsService.getManyBaseUoms,
              getLabel: (opt) => opt?.name,
              requestPayload: {
                filters: {
                  isBase: 1,
                },
              },
              placeholder: getValues('uom'),
              size: Size.MD,
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="salesUom"
            label={t('labelSalesUom')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="externalUom"
            label={t('labelPartExternalUom')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="packageUom"
            label={t('labelPackageUom')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pkgUnits"
            label={t('labelPackageQuantity')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="bomPriceDecimals"
            label={t('labelJobPriceDecimals')}
            labelPosition={labelPosition}
            required={true}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="vendorNumber"
            label={t('labelCompanyPartNumber')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="companyManufacturerName"
            label={t('labelCompanyManufacturerName')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="companyManufacturerId"
            label={t('labelCompanyManufacturerId')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item md={6} xs={12}>
          <Field
            name="companyPartStatus"
            type={InputType.SELECT}
            label={t('labelStatus')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              placeholder: '',
              options: partStatus,
              size: Size.MD,
              ...disabledState,
            }}
          />

          <Field
            name="mfgPartStatus"
            type={InputType.SELECT}
            label={t('labelManufacturerStatus')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              placeholder: '',
              options: partStatus,
              size: Size.MD,
              ...disabledState,
            }}
          />

          <Field
            name="replacementPart"
            type={InputType.TEXTFIELD}
            label={t('labelReplacement')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            name="taaStatus"
            type={InputType.SELECT}
            label={t('labelTaaStatus')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              placeholder: '',
              options: taaStatus,
              size: Size.MD,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="manufacturerMinimumQuantity"
            label={t('labelVendorMoq')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="minimumQuantity"
            label={t('labelSalesMoq')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="manufacturerOrderIncrement"
            label={t('labelVendorOrderIncrements')}
            labelPosition={labelPosition}
            required={true}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="orderIncrement"
            label={t('labelSalesOrderIncrements')}
            labelPosition={labelPosition}
            required={true}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="quotePriceDecimals"
            label={t('labelQuoteDecimals')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="part"
            label={t('labelExternalPartNumber')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
              disabled: true,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="manufacturerName"
            label={t('labelExternalVendorName')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
              disabled: true,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="manufacturerId"
            label={t('labelExternalVendorId')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
              disabled: true,
            }}
          />
        </Grid>
      </GridContainer>

      <SectionTitle>{t('labelPricingGroups')}</SectionTitle>

      <GridContainer container isDisabled={isDisabled} spacing={2}>
        <Grid item xs={12} md={6}>
          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroup1"
            label={t('labelPricingGroup1')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroup2"
            label={t('labelPricingGroup2')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroup3"
            label={t('labelPricingGroup3')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroup4"
            label={t('labelPricingGroup4')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroup5"
            label={t('labelPricingGroup5')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroupDescription1"
            label={t('labelPricingGroupDescription1')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroupDescription2"
            label={t('labelPricingGroupDescription2')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroupDescription3"
            label={t('labelPricingGroupDescription3')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroupDescription4"
            label={t('labelPricingGroupDescription4')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />

          <Field
            type={InputType.TEXTFIELD}
            name="pricingGroupDescription5"
            label={t('labelPricingGroupDescription5')}
            labelPosition={labelPosition}
            inputProps={{
              control,
              ...disabledState,
            }}
          />
        </Grid>
      </GridContainer>
    </>
  );
};

export default withQueryState(CompanyPartDetailsFormView);
